import * as React from "react";

export type AppRoute =
  | { name: "home"; path: "/" }
  | { name: "room"; roomRef: string; invited: boolean; path: string }
  | { name: "profile"; handle: string; path: string }
  | { name: "profile-import"; handle: string; path: string }
  | { name: "not-found"; path: string };

function routeFromLocation(): AppRoute {
  const url = new URL(window.location.href);
  const legacyRoom = url.searchParams.get("room");
  if (legacyRoom) {
    const path = `/rooms/${encodeURIComponent(legacyRoom)}`;
    window.history.replaceState({}, "", path);
    return { name: "room", roomRef: legacyRoom, invited: false, path };
  }
  const parts = url.pathname.split("/").filter(Boolean);
  if (!parts.length) return { name: "home", path: "/" };
  if (parts[0] === "rooms" && parts[1]) {
    return { name: "room", roomRef: decodeURIComponent(parts[1]), invited: false, path: url.pathname };
  }
  if (parts[0] === "invite" && parts[1]) {
    return { name: "room", roomRef: decodeURIComponent(parts[1]), invited: true, path: url.pathname };
  }
  if (parts[0] === "u" && parts[1]) {
    if (parts[2] === "spotify-import") {
      return { name: "profile-import", handle: decodeURIComponent(parts[1]), path: url.pathname };
    }
    return { name: "profile", handle: decodeURIComponent(parts[1]), path: url.pathname };
  }
  return { name: "not-found", path: url.pathname };
}

interface NavigationContextValue {
  route: AppRoute;
  navigate: (path: string, options?: { replace?: boolean }) => void;
}

const NavigationContext = React.createContext<NavigationContextValue | null>(null);

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const [route, setRoute] = React.useState<AppRoute>(() => routeFromLocation());
  React.useEffect(() => {
    const onPopState = () => setRoute(routeFromLocation());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = React.useCallback((path: string, options?: { replace?: boolean }) => {
    const commit = () => {
      window.history[options?.replace ? "replaceState" : "pushState"]({}, "", path);
      setRoute(routeFromLocation());
      window.scrollTo({ top: 0, behavior: "instant" });
    };
    const transition = (document as Document & { startViewTransition?: (callback: () => void) => void }).startViewTransition;
    if (transition && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      transition.call(document, commit);
    } else {
      commit();
    }
  }, []);

  return <NavigationContext.Provider value={{ route, navigate }}>{children}</NavigationContext.Provider>;
}

export function useNavigation() {
  const context = React.useContext(NavigationContext);
  if (!context) throw new Error("useNavigation must be used inside NavigationProvider");
  return context;
}

export function AppLink({ href, className, children, onClick, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const { navigate } = useNavigation();
  return (
    <a
      {...props}
      href={href}
      className={className}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigate(href);
      }}
    >
      {children}
    </a>
  );
}
